/*
 *
 * THIS ENTIRE FILE WAS COPY-PASTED FROM SKYSLOPE-MACHE
 * @see https://github.com/skyslope/skyslope-mache/blob/main/lib/components/loader-button/loader-button.tsx
 * When mache is finally updated in this project, this file should be deleted
 *
 */

import React from 'react';
import { Button, type ButtonProps, CircularProgress } from '@material-ui/core';
import { twMerge } from 'tailwind-merge';

export type LoaderButtonProps<C extends React.ElementType = 'button'> = ButtonProps<C> & {
  /**
   * The component used for the root node.
   * Either a string to use an HTML element or a component.
   */
  component?: C;
  isLoading?: boolean;
  /**
   * Show this button as loading until a specific promise resolves.
   *
   * This prop is particularly useful when you are mapping over an array to render
   * multiple instances of a single button, and you only want to show a loading state
   * on the button was clicked (rather than all of them).
   */
  onClickAsync?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => Promise<unknown>;
};

/**
 * Same as the MUI button except it can optionally show a spinner in the center.
 */
export function LoaderButton<C extends React.ElementType = 'button'>(props: LoaderButtonProps<C>) {
  const { children, className, isLoading, disabled, onClick, onClickAsync, ...rest } = props;
  const [isPromiseLoading, setIsPromiseLoading] = React.useState(false);
  const finalIsLoading = isLoading || isPromiseLoading;

  return (
    <Button
      {...rest}
      disabled={disabled || finalIsLoading}
      className={twMerge('relative rounded-full py-4 px-6', className)}
      onClick={(e) => {
        onClick?.(e);

        const promise = onClickAsync?.(e);
        if (!promise) return;
        setIsPromiseLoading(true);
        promise.finally(() => setIsPromiseLoading(false));
      }}
    >
      {children}
      {finalIsLoading && (
        <span
          className="absolute top-1/2 left-1/2"
          style={{ transform: 'translate3d(-50%, calc(-50% + 2px), 0)' }}
          aria-label="Loading"
        >
          <CircularProgress size={24} color="primary" />
        </span>
      )}
    </Button>
  );
}
